import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Video } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : 'http://localhost:8001/api';

const AdminCoursesManagement = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lessonDialogOpen, setLessonDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    price: 0,
    image_url: '',
    duration: '',
    level: 'Beginner',
    category: '',
    average_salary_india: '',
    average_salary_global: '',
    is_popular: false,
    subscription_discounts: {},
  });
  
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  
  const [lessonForm, setLessonForm] = useState({
    title: '',
    video_url: '',
    duration_minutes: 0,
    description: '',
    order: 1,
    is_preview: false,
  });

  useEffect(() => {
    if (!token || user?.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchCourses();
    fetchSubscriptionPlans();
  }, [token, user, navigate]);

  const fetchSubscriptionPlans = async () => {
    try {
      const response = await axios.get(`${API}/admin/subscription-plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscriptionPlans(response.data || []);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      toast.error('Failed to load subscription plans');
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses/admin/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCourse = async () => {
    try {
      const formData = {
        ...courseForm,
        price: parseFloat(courseForm.price) || 0,
      };

      if (editingCourse) {
        await axios.put(
          `${API}/courses/admin/${editingCourse.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Course updated successfully!');
      } else {
        await axios.post(
          `${API}/courses/admin`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Course created successfully!');
      }

      fetchCourses();
      setDialogOpen(false);
      resetCourseForm();
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error(error.response?.data?.detail || 'Failed to save course');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;

    try {
      await axios.delete(`${API}/courses/admin/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Course deleted successfully!');
      fetchCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete course');
    }
  };

  const handleSaveLesson = async () => {
    if (!selectedCourse) return;

    try {
      const formData = {
        ...lessonForm,
        duration_minutes: parseInt(lessonForm.duration_minutes) || 0,
        order: parseInt(lessonForm.order) || 1,
      };

      await axios.post(
        `${API}/courses/admin/${selectedCourse.id}/lessons`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lesson added successfully!');

      fetchCourses();
      resetLessonForm();
    } catch (error) {
      console.error('Error saving lesson:', error);
      toast.error(error.response?.data?.detail || 'Failed to save lesson');
    }
  };

  const openEditCourse = (course) => {
    setEditingCourse(course);
    setCourseForm({
      title: course.title || '',
      description: course.description || '',
      price: course.price || 0,
      image_url: course.image_url || '',
      duration: course.duration || '',
      level: course.level || 'Beginner',
      category: course.category || '',
      average_salary_india: course.average_salary_india || '',
      average_salary_global: course.average_salary_global || '',
      is_popular: course.is_popular || false,
      subscription_discounts: course.subscription_discounts || {},
    });
    setDialogOpen(true);
  };

  const resetCourseForm = () => {
    setEditingCourse(null);
    setCourseForm({
      title: '',
      description: '',
      price: 0,
      image_url: '',
      duration: '',
      level: 'Beginner',
      category: '',
      average_salary_india: '',
      average_salary_global: '',
      is_popular: false,
      subscription_discounts: {},
    });
  };

  const resetLessonForm = () => {
    setLessonForm({
      title: '',
      video_url: '',
      duration_minutes: 0,
      description: '',
      order: 1,
      is_preview: false,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Courses Management</h1>
            <p className="text-gray-600">Create and manage courses with lessons</p>
          </div>
          <Button
            onClick={() => {
              resetCourseForm();
              setDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Course
          </Button>
        </div>

        {courses.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <p className="text-gray-600 mb-4">No courses yet. Create your first course!</p>
              <Button onClick={() => {
                resetCourseForm();
                setDialogOpen(true);
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Create Course
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <Card key={course.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  {course.image_url && (
                    <div className="w-full h-48 rounded-md overflow-hidden mb-4">
                      <img
                        src={course.image_url}
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-xl">{course.title}</CardTitle>
                    {course.is_popular && (
                      <Badge>Popular</Badge>
                    )}
                  </div>
                </CardHeader>

                <CardContent>
                  <div className="space-y-2 mb-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Fee:</span>
                      <span className="font-medium">₹{course.price}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Lessons:</span>
                      <span className="font-medium">{course.lessons?.length || 0}</span>
                    </div>
                  </div>

                  {course.subscription_discounts && Object.keys(course.subscription_discounts).length > 0 && (
                    <div className="mb-4 p-2 bg-green-50 rounded text-xs">
                      <p className="font-semibold text-gray-700 mb-1">Subscription Discounts:</p>
                      {Object.entries(course.subscription_discounts).map(([planName, discount]) => (
                        discount > 0 && (
                          <div key={planName} className="flex justify-between">
                            <span className="text-gray-600 capitalize">{planName}:</span>
                            <span className="font-medium text-green-700">{discount}% off</span>
                          </div>
                        )
                      ))}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        setSelectedCourse(course);
                        resetLessonForm();
                        setLessonDialogOpen(true);
                      }}
                    >
                      <Video className="h-4 w-4 mr-2" />
                      Manage Lessons
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => openEditCourse(course)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteCourse(course.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Course Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCourse ? 'Edit Course' : 'Create New Course'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Course Title</Label>
              <Input
                value={courseForm.title}
                onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                placeholder="Complete Web Development"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={courseForm.description}
                onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                placeholder="Course description..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price (₹)</Label>
                <Input
                  type="number"
                  value={courseForm.price}
                  onChange={(e) => setCourseForm({ ...courseForm, price: e.target.value })}
                  placeholder="2999"
                />
              </div>
              <div>
                <Label>Duration</Label>
                <Input
                  value={courseForm.duration}
                  onChange={(e) => setCourseForm({ ...courseForm, duration: e.target.value })}
                  placeholder="8 weeks"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Level</Label>
                <Input
                  value={courseForm.level}
                  onChange={(e) => setCourseForm({ ...courseForm, level: e.target.value })}
                  placeholder="Beginner"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Input
                  value={courseForm.category}
                  onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })}
                  placeholder="Technology"
                />
              </div>
            </div>

            <div>
              <Label>Image URL</Label>
              <Input
                value={courseForm.image_url}
                onChange={(e) => setCourseForm({ ...courseForm, image_url: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Avg Salary (India)</Label>
                <Input
                  value={courseForm.average_salary_india}
                  onChange={(e) => setCourseForm({ ...courseForm, average_salary_india: e.target.value })}
                  placeholder="₹6-20 LPA"
                />
              </div>
              <div>
                <Label>Avg Salary (Global)</Label>
                <Input
                  value={courseForm.average_salary_global}
                  onChange={(e) => setCourseForm({ ...courseForm, average_salary_global: e.target.value })}
                  placeholder="$80K-150K"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_popular"
                checked={courseForm.is_popular}
                onChange={(e) => setCourseForm({ ...courseForm, is_popular: e.target.checked })}
              />
              <Label htmlFor="is_popular">Mark as Popular</Label>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Subscription Plan Discounts</h3>
              <p className="text-sm text-gray-600 mb-3">
                Set discount percentage for each subscription plan. Use 100% for free access.
                {subscriptionPlans.length === 0 && ' (Loading plans...)'}
              </p>
              
              {subscriptionPlans.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {subscriptionPlans
                    .filter(plan => plan.is_active && plan.name !== 'free')
                    .map((plan) => (
                    <div key={plan.id}>
                      <Label htmlFor={`discount-${plan.id}`}>
                        {plan.display_name} Plan Discount (%)
                      </Label>
                      <Input
                        id={`discount-${plan.id}`}
                        type="number"
                        min="0"
                        max="100"
                        value={courseForm.subscription_discounts?.[plan.name] || 0}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0;
                          const clampedValue = Math.min(Math.max(value, 0), 100);
                          setCourseForm({ 
                            ...courseForm, 
                            subscription_discounts: {
                              ...courseForm.subscription_discounts,
                              [plan.name]: clampedValue
                            }
                          });
                        }}
                        placeholder="0-100"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        0% = No discount, 100% = Free access
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  No active subscription plans found. Create plans in Admin Panel → Payments → Subscription Plans.
                </div>
              )}
              
              {subscriptionPlans.length > 0 && (
                <div className="mt-3 p-3 bg-blue-50 rounded-md text-xs text-blue-800">
                  <p className="font-medium mb-1">💡 Note:</p>
                  <p>Discounts are automatically applied when users with these subscription plans enroll in this course.</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCourse}>
              {editingCourse ? 'Update Course' : 'Create Course'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lesson Dialog */}
      <Dialog open={lessonDialogOpen} onOpenChange={setLessonDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Manage Lessons - {selectedCourse?.title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <h4 className="font-semibold mb-3">Existing Lessons ({selectedCourse?.lessons?.length || 0})</h4>
              {selectedCourse?.lessons && selectedCourse.lessons.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto mb-4">
                  {selectedCourse.lessons.map((lesson) => (
                    <div key={lesson.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm">{lesson.order}. {lesson.title}</span>
                      <span className="text-xs text-gray-500">{lesson.duration_minutes} min</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 mb-4">No lessons yet</p>
              )}
            </div>

            <div>
              <Label>Lesson Title</Label>
              <Input
                value={lessonForm.title}
                onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                placeholder="Introduction to React"
              />
            </div>

            <div>
              <Label>Video URL</Label>
              <Input
                value={lessonForm.video_url}
                onChange={(e) => setLessonForm({ ...lessonForm, video_url: e.target.value })}
                placeholder="https://youtube.com/..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={lessonForm.duration_minutes}
                  onChange={(e) => setLessonForm({ ...lessonForm, duration_minutes: e.target.value })}
                  placeholder="15"
                />
              </div>
              <div>
                <Label>Order</Label>
                <Input
                  type="number"
                  value={lessonForm.order}
                  onChange={(e) => setLessonForm({ ...lessonForm, order: e.target.value })}
                  placeholder="1"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={lessonForm.description}
                onChange={(e) => setLessonForm({ ...lessonForm, description: e.target.value })}
                placeholder="Lesson description..."
                rows={2}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_preview"
                checked={lessonForm.is_preview}
                onChange={(e) => setLessonForm({ ...lessonForm, is_preview: e.target.checked })}
              />
              <Label htmlFor="is_preview">Allow preview</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLessonDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={handleSaveLesson}>
              Add Lesson
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCoursesManagement;